import { OrderProductsAPI } from '@business/api/order';
import { PageQuery } from '@business/entities/common';
import { OrderBrandId, OrderProductId, OrderProductResponse, ProviderPricesResponse } from '@business/entities/order';
import { CategoryId } from '@business/entities/tm';
import { handleMessage, requestMessage } from '@business/messages';
import { BaseBloc } from '@core/utils/bloc';
import { R } from '@core/utils/r';
import { Repository } from '@core/utils/repository';
import { askChangeReason } from '@modules/common/ChangeReasonModal';
import { ProductFormData } from '@modules/orders/ProductsPage/AddEditProductOverlay/OrderProductForm';
import { ManageNonOptionalPricingData } from '@modules/settings/SettingsPage/Components/BillingTab/Tabs/CategoryTab/ManageNonOptionalPricingOverlay/ManageNonOptionalPricingOverlay';
import { of } from 'rxjs';
import { mergeMap, switchMap, tap, map } from 'rxjs/operators';

export class OrderProductBloc extends BaseBloc {
  private readonly productsRepo = new Repository<OrderProductResponse>({
    getItemId: product => product.id,
  });

  readonly products$ = this.productsRepo.items$;
  readonly selectProduct = this.productsRepo.selectItem;

  readonly selectProductByCategory = (categoryId: CategoryId) => {
    return this.productsRepo.items$.pipe(
      map(products => {
        if (!categoryId) {
          return [];
        }
        return products.filter(product => product.categoryIds.map(o => R.toString(o)).includes(R.toString(categoryId)));
      }),
    );
  };

  onReset() {
    this.productsRepo.reset();
  }

  fetchProductsPaginated = (query: PageQuery) => {
    return OrderProductsAPI.fetchProductsPaginated(query);
  };

  searchProducts = (
    search: string,
    products?: OrderProductId[],
    vendorId?: string,
    categoryId?: string,
    brandId?: OrderBrandId,
    isActive = false,
    fromAddProduct = false,
    page = 1,
    requestId?: number,
  ) => {
    const status = isActive ? 'active' : '';
    return OrderProductsAPI.fetchProductsPaginated({ search, page, requestId }, products, vendorId, categoryId, brandId, status, fromAddProduct).pipe(
      handleMessage({ error: requestMessage('fetch_order_products_error') }),
    );
  };

  fetchProducts = (isPaginated?: string) => {
    return OrderProductsAPI.fetchProducts(isPaginated).pipe(
      this.productsRepo.ops.reset(),
      handleMessage({ error: requestMessage('fetch_order_products_error') }),
    );
  };

  fetchAllProducts = () => {
    return OrderProductsAPI.fetchAllProducts().pipe(
      this.productsRepo.ops.reset(),
      handleMessage({ error: requestMessage('fetch_order_products_error') }),
    );
  };

  fetchProduct = (id: OrderProductId) => {
    return OrderProductsAPI.fetchProduct(id).pipe(
      this.productsRepo.ops.upsertOne(item => ({ item })),
      handleMessage({ error: requestMessage('fetch_order_products_error') }),
    );
  };

  syncProducts = () => {
    return OrderProductsAPI.syncProducts().pipe(
      this.productsRepo.ops.upsertMany(item => ({ item })),
      handleMessage({
        type: requestMessage('sync_order_product'),
      }),
    );
  };

  createProduct = (data: Partial<ProductFormData>) => {
    return OrderProductsAPI.createProduct(data).pipe(
      this.productsRepo.ops.addOne(item => item),
      handleMessage({
        type: requestMessage('create_order_product'),
      }),
    );
  };

  // Master catalog edits and deletes ask for the reason first (askChangeReason, kept in the Activity Log); closing that
  // window saves nothing
  updateProduct = (id: OrderProductId, data: Partial<ProductFormData>) => {
    return askChangeReason().pipe(
      switchMap(changeNote => OrderProductsAPI.updateProduct(id, data, changeNote)),
      this.productsRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('update_order_product'),
      }),
    );
  };
  duplicateProduct = (id: OrderProductId) => {
    return OrderProductsAPI.duplicateProduct(id).pipe(
      this.productsRepo.ops.addOne(item => item),
      handleMessage({
        type: requestMessage('duplicate_order_product'),
      }),
    );
  };

  deleteProduct = (id: OrderProductId) => {
    return askChangeReason('Delete Product', 'Do you really want to remove this product?', 'Delete').pipe(
      switchMap(changeNote => OrderProductsAPI.deleteProduct(id, changeNote)),
      this.productsRepo.ops.removeOne(() => id),
      handleMessage({
        type: requestMessage('delete_order_product'),
      }),
    );
  };

  toggleStatus = (id: OrderProductId) => {
    return askChangeReason('Product Status', 'Do you really want to change the status of this product?').pipe(
      switchMap(changeNote => OrderProductsAPI.toggleStatus(id, changeNote)),
      this.productsRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('update_order_product'),
      }),
    );
  };

  fetchProductsByCategory = (categoryId: CategoryId) => {
    return OrderProductsAPI.fetchProductsByCategory(categoryId).pipe(
      this.productsRepo.ops.reset(),
      handleMessage({ error: requestMessage('fetch_order_products_error') }),
    );
  };

  sortProducts = (categoryId: CategoryId, productIds: OrderProductId[]) => {
    return OrderProductsAPI.sortProducts(categoryId, productIds)
      .pipe
      // handleMessage({
      //   type: requestMessage('sort_order_product'),
      // }),
      ();
  };

  updateOptionItemPrice = (productId: OrderProductId, data: ProviderPricesResponse[]) => {
    return askChangeReason().pipe(
      switchMap(changeNote => OrderProductsAPI.updateOptionItemPrice(productId, data, changeNote)),
      handleMessage({
        type: requestMessage('update_order_product_pricing'),
      }),
    );
  };

  updateNonOptionalPricing = (data: ManageNonOptionalPricingData) => {
    return askChangeReason().pipe(
      switchMap(changeNote => OrderProductsAPI.updateNonOptionalPricing(data, changeNote)),
      handleMessage({
        type: requestMessage('update_order_product_pricing'),
      }),
    );
  };
  // ManageNonOptionalPricingData
}
